import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Star, Download, Lock, ShieldCheck } from "lucide-react";
import { HOTMART_URL } from "@/lib/site";

// Smooth section entrance
export const Reveal = ({ children, delay = 0, className = "" }) => {
  const reduce = useReducedMotion();
  if (reduce) return <div className={className}>{children}</div>;
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.55, ease: "easeOut", delay }}
    >
      {children}
    </motion.div>
  );
};

// Primary CTA that opens Hotmart in a new tab
export const HotmartButton = ({
  children,
  testId,
  className = "",
  variant = "primary",
}) => {
  const base =
    "inline-flex items-center justify-center gap-2 rounded-xl font-extrabold transition-[transform,box-shadow,filter] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] focus-visible:ring-offset-2 active:scale-[0.98]";
  const variants = {
    primary:
      "h-14 px-7 text-base sm:text-lg bg-[hsl(var(--primary))] text-white shadow-[0_12px_26px_rgba(244,107,93,0.32)] hover:brightness-[1.04] hover:shadow-[0_16px_34px_rgba(244,107,93,0.4)]",
    big:
      "h-16 px-8 text-lg sm:text-xl bg-[hsl(var(--primary))] text-white shadow-[0_14px_30px_rgba(244,107,93,0.35)] hover:brightness-[1.04] hover:shadow-[0_18px_40px_rgba(244,107,93,0.45)]",
  };
  return (
    <a
      href={HOTMART_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Comprar el ebook en Hotmart (se abre en una nueva pestaña)"
      data-testid={testId}
      className={`${base} ${variants[variant]} ${className}`}
    >
      {children}
    </a>
  );
};

export const Stars = ({ count = 5, className = "" }) => (
  <div className={`flex items-center gap-0.5 ${className}`} aria-label={`${count} de 5 estrellas`}>
    {Array.from({ length: count }).map((_, i) => (
      <Star key={i} className="h-4 w-4 fill-[hsl(var(--brand-sun))] text-[hsl(var(--brand-sun))]" />
    ))}
  </div>
);

export const ReassuranceRow = ({ className = "" }) => (
  <div className={`flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground ${className}`}>
    <span className="inline-flex items-center gap-1.5">
      <Download className="h-4 w-4 text-[hsl(var(--brand-coral))]" /> Descarga inmediata
    </span>
    <span className="inline-flex items-center gap-1.5">
      <Lock className="h-4 w-4 text-[hsl(var(--brand-coral))]" /> Pago seguro
    </span>
    <span className="inline-flex items-center gap-1.5">
      <ShieldCheck className="h-4 w-4 text-[hsl(var(--brand-coral))]" /> Garantía 7 días
    </span>
  </div>
);

export const SectionTitle = ({ eyebrow, title, subtitle, center = true }) => (
  <div className={`max-w-2xl ${center ? "mx-auto text-center" : ""}`}>
    {eyebrow && (
      <span className="inline-block rounded-full bg-[hsl(var(--brand-sun))]/40 px-3 py-1 text-xs font-bold uppercase tracking-wide text-[hsl(var(--brand-ink))]">
        {eyebrow}
      </span>
    )}
    <h2 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-[hsl(var(--brand-ink))] sm:text-3xl md:text-4xl">
      {title}
    </h2>
    {subtitle && (
      <p className="mt-3 text-base leading-relaxed text-muted-foreground sm:text-lg">{subtitle}</p>
    )}
  </div>
);
